import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Dropdown from "../../components/Dropdown";
import {
  List,
  ListItem,
  Text,
  Button,
  Checkbox,
  Spinner,
  Box,
  Heading,
  Input,
  InputGroup,
  Flex,
  Center,
  useToast,
  IconButton,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { PiFileMagnifyingGlassDuotone } from "react-icons/pi";

const TrackTrace = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const tableRef = useRef(null);
  // Sets list of logs used to populate table
  const [logs, setLogs] = useState([]);
  // From fetchTrackTraceLog request response used for table header filter dropdown inputs
  const [departments, setDepartments] = useState([]);
  const [hrbps, setHrbps] = useState([]);
  const [reports, setReports] = useState([]);
  const [requestors, setRequestors] = useState([]);
  const [statuses, setStatuses] = useState([]);
  // Used for filtering in fetchTrackTraceLog request
  const [searchTerm, setSearchTerm] = useState("");
  const [logDropdownFilter, setLogDropdownFilter] = useState({});
  // State for checkbox at top of table
  const [showInProgressLogs, setShowInProgressLogs] = useState(true);
  const [loading, setLoading] = useState(true);

  const UUID = localStorage.getItem("UUID");
  const HuserID = localStorage.getItem("HuserID");

  const API_BASE_URL = "https://qaapirgeneng.digitalfields.co.za/ws1.cfc";

  const fetchTrackTraceLog = useCallback(
    async (searchKey = "", showInProgress = true, dropdownFilter = {}) => {
      try {
        setLoading(true);
        let url = `${API_BASE_URL}?method=DashboardRequests&huserID=${HuserID}&uuid=${UUID}&HclientID=11&inProgressYN=${showInProgress}`;

        if (searchKey.length >= 2) {
          url += `&searchkey=${encodeURIComponent(searchKey)}`;
        }

        let response = await fetch(url, {
          method: "POST",
          // Maybe we can find out why the request doesn't like the headers params
          // headers: {
          //     "Content-Type": "application/json",
          // },
          body: JSON.stringify(dropdownFilter),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setLogs(data[0].listArray);
        setDepartments(data[0].departments);
        setHrbps(data[0].HRBPs);
        setReports(data[0].batteries);
        setRequestors(data[0].requestors);
        setStatuses(data[0].statuses);
      } catch (error) {
        console.error("Error fetching tracking log:", error);
        toast({
          title: "Error fetching tracking log",
          description: "",
          status: "error",
          duration: 6000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    },
    [HuserID, UUID, toast]
  );

  useEffect(() => {
    fetchTrackTraceLog(searchTerm, showInProgressLogs, logDropdownFilter);
  }, [fetchTrackTraceLog, showInProgressLogs, logDropdownFilter]);

  const handleSearch = () => {
    if (searchTerm.length >= 2) {
      fetchTrackTraceLog(searchTerm);
    } else if (!searchTerm.length) {
      fetchTrackTraceLog(searchTerm);
      setLogDropdownFilter({});
    } else {
      toast({
        title: "Search term too short",
        description: "Please enter at least 2 characters to search",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleChange = (e) => {
    let { name, value } = e.target;

    if (name === "department") {
      setLogDropdownFilter((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    } else {
      setLogDropdownFilter((prevState) => ({
        ...prevState,
        [name]: Number(value),
      }));
    }
  };

  const handleCheckBoxChange = (e) => {
    const { checked } = e.target;

    setShowInProgressLogs(checked);
  };

  const handleClick = (e) => {
    const { name } = e.target;

    if (name === "clearFilters") {
      setSearchTerm("");
      setLogDropdownFilter({});
    }
  };

  // Function to navigate to readOnlyRequest page
  const handleViewRequest = (HrequestID) => {
    navigate(`/readonlyrequest/${HrequestID}`);
  };

  // Loading spinner
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="30vh"
      >
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Box p={6} mb={8}>
      <Heading mb={5}>Track & Trace Log</Heading>
      <Flex align="center" mb={5}>
        <Flex gap={2} alignItems="center">
          {/* Candidate & Position free text search input */}
          <InputGroup width="300px">
            <Input
              placeholder="Search candidate / position"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
          <Button
            display="flex"
            alignItems="center"
            justifyContent="center"
            w="40px"
            p={0}
            onClick={handleSearch}
          >
            <SearchIcon />
          </Button>
          {/* Show In Progress Logs checkbox input */}
          <Checkbox
            colorScheme="orange"
            name="logsInProgress"
            isChecked={showInProgressLogs}
            value={showInProgressLogs}
            onChange={handleCheckBoxChange}
            ml="8"
          >
            Show In Progress
          </Checkbox>
          {/* Clear Filters button */}
          <Button
            colorScheme="orange"
            name="clearFilters"
            onClick={handleClick}
            ml="8"
          >
            Clear Filters
          </Button>
        </Flex>
      </Flex>

      {/* Track & Trace Log Table */}
      <List ref={tableRef}>
        {/* Table Header section */}
        <ListItem border="2px" borderColor="gray.600">
          <Flex>
            <Dropdown
              width="9.5%"
              height="100%"
              placeHolder="Department"
              name="department"
              options={departments}
              optionKey="departmentID"
              optionDisplay="department"
              state={logDropdownFilter}
              handleChange={handleChange}
              required={false}
            />
            <Center
              w="9.5%"
              fontWeight="bold"
              bg="gray.50"
              border="1px solid #e7e7e7"
            >
              Position
            </Center>
            <Dropdown
              width="9.5%"
              height="100%"
              placeHolder="HRBP"
              name="hrbp"
              options={hrbps}
              optionKey="HRBPID"
              optionDisplay="HRBPname"
              state={logDropdownFilter}
              handleChange={handleChange}
              required={false}
            />
            <Dropdown
              width="9.5%"
              height="100%"
              placeHolder="Requestor"
              name="requestor"
              options={requestors}
              optionKey="requestorID"
              optionDisplay="requestorname"
              state={logDropdownFilter}
              handleChange={handleChange}
              required={false}
            />
            <Dropdown
              width="9.5%"
              height="100%"
              placeHolder="Status"
              name="status"
              options={statuses}
              optionKey="DDrequestStatusID"
              optionDisplay="statusText"
              state={logDropdownFilter}
              handleChange={handleChange}
              required={false}
            />
            <Center
              w="9.5%"
              fontWeight="bold"
              bg="gray.50"
              border="1px solid #e7e7e7"
            >
              Candidate
            </Center>
            <Center
              w="9.5%"
              fontWeight="bold"
              bg="gray.50"
              border="1px solid #e7e7e7"
            >
              Booking Date
            </Center>
            <Dropdown
              width="9.5%"
              height="100%"
              placeHolder="Report Name"
              name="report"
              options={reports}
              optionKey="HbatteryID"
              optionDisplay="batteryName"
              state={logDropdownFilter}
              handleChange={handleChange}
              required={false}
            />
            <Center
              p="2"
              w="9.5%"
              fontWeight="bold"
              bg="gray.50"
              border="1px solid #e7e7e7"
            >
              Report Uploaded Date
            </Center>
            <Center
              w="9.5%"
              fontWeight="bold"
              bg="gray.50"
              border="1px solid #e7e7e7"
            >
              Last Update
            </Center>
            <Center
              p="2"
              w="9.5%"
              fontWeight="bold"
              bg="gray.50"
              border="1px solid #e7e7e7"
            >
              Time Since Last Update
            </Center>
            <Center
              w="5%"
              fontWeight="bold"
              bg="gray.50"
              border="1px solid #e7e7e7"
            >
              View
            </Center>
          </Flex>
        </ListItem>
        {/* Table Body section */}
        {logs.map((log) => (
          <ListItem
            key={log.HrequestID}
            borderBottom="2px"
            borderLeft="2px"
            borderRight="2px"
            borderColor="gray.600"
          >
            {/* Log Information */}
            <Flex justify={"space-between"}>
              <Text variant={"tableBodyCell"} w="9.5%" p="1">
                {log.department}
              </Text>
              <Text variant={"tableBodyCell"} w="9.5%" p="1">
                {log.position}
              </Text>
              <Text variant={"tableBodyCell"} w="9.5%" p="1">
                {log.HRBP}
              </Text>
              <Text variant={"tableBodyCell"} w="9.5%" p="1">
                {log.requestor}
              </Text>
              <Text variant={"tableBodyCell"} w="9.5%" p="1">
                {log.statusText}
              </Text>
              <Text variant={"tableBodyCell"} w="9.5%" p="1">
                {log.candidate}
              </Text>
              <Text variant={"tableBodyCell"} w="9.5%" p="1">
                {log.bookingDate}
              </Text>
              <Text variant={"tableBodyCell"} w="9.5%" p="1">
                {log.battery}
              </Text>
              <Text variant={"tableBodyCell"} w="9.5%" p="1">
                {log.firstReportUploadDate}
              </Text>
              <Text variant={"tableBodyCell"} w="9.5%" p="1">
                {log.lastUpdate}
              </Text>
              <Text variant={"tableBodyCell"} w="9.5%" p="1">
                {log.timeSinceLastUpdate}
              </Text>
              <Center w="5%" p="1">
                <IconButton
                  icon={<PiFileMagnifyingGlassDuotone size="2em" />}
                  onClick={() => handleViewRequest(log.HrequestID)}
                  aria-label="View request details"
                  size="lg"
                  colorScheme="orange"
                  variant="ghost"
                  title="View Request Details"
                />
              </Center>
            </Flex>
            {/* Progress Bar */}
            <Flex>
              <Box
                h="5px"
                w={`${log.progress[0].done}%`}
                bg="rgba(248, 100, 30, 0.7)"
              ></Box>
              <Box h="5px" w={`${log.progress[0].todo}%`} bg="gray.100"></Box>
            </Flex>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default TrackTrace;
